import moment from 'moment';
import find from 'lodash/find';
import unionBy from 'lodash/unionBy';

import createWsAction from 'utils/create-ws-action';
import {
	liveDiscoverySelector,
	liveThermalDiscoverySelector,
	liveSecurityMessagingSelector,
	liveFaceDiscoverySelector,
	liveWifiDiscoverySelector,
} from 'redux/selectors';
import { referenceMap } from 'utils/helpers';
import { API_SUBSCRIBE_UPDATE } from 'redux/api/constants';
import { ZONE_ENTITIES_INACTIVE_THRESHOLD } from 'config/constants';

// ------------------------------------
// Action Types
// ------------------------------------
export const SITES_LIST_FETCH = 'unifi.SITES_LIST_FETCH';
export const SITE_GET = 'unifi.SITE_GET'
export const SITE_UPDATE = 'unifi.SITE_UPDATE'
export const SITE_CREATE = 'unifi.SITE_CREATE';
export const ZONES_LIST_FETCH = 'unifi.ZONES_LIST_FETCH';
export const ZONE_GET = 'unifi.ZONE_GET'
export const ZONE_UPDATE = 'unifi.ZONE_UPDATE'
export const ZONE_CREATE = 'unifi.ZONE_CREATE';
export const READERS_LIST_FETCH = 'unifi.READERS_LIST_FETCH';
export const READER_CREATE = 'unifi.READER_CREATE';
export const READER_GET = 'unifi.READER_GET'
export const READER_UPDATE = 'unifi.READER_UPDATE'
export const ANTENNAS_LIST_FETCH = 'unifi.ANTENNAS_LIST_FETCH';
export const ANTENNA_CREATE = 'unifi.ANTENNA_CREATE';

export const ZONE_ENTITIES_CLEAR_INACTIVE =
	'unifi.ZONE_ENTITIES_CLEAR_INACTIVE';
export const ZONE_ENTITIES_SUBSCRIBE = 'unifi.ZONE_ENTITIES_SUBSCRIBE';
export const THERMAL_ENTITIES_SUBSCRIBE = 'unifi.THERMAL_ENTITIES_SUBSCRIBE';
export const FACE_ENTITIES_SUBSCRIBE = 'unifi.FACE_ENTITIES_SUBSCRIBE';
export const WIFI_ENTITIES_SUBSCRIBE = 'unifi.WIFI_ENTITIES_SUBSCRIBE';
export const ZONE_LIST_FETCH = 'unifi.ZONE_LIST_FETCH';
export const CHECKIN_CHECKOUT_ZONE_LIST_FETCH = 'unifi.CHECKIN_CHECKOUT_ZONE_LIST_FETCH';
export const ZONE_LIST_ACCUMULATE_BY_SITE =
	'unifi.ZONE_LIST_ACCUMULATE_BY_SITE';

// ------------------------------------
// Helper functions
// ------------------------------------
const filterOutInactiveEntities = liveDiscovery =>
	liveDiscovery.filter(item => {
		return (
			moment().diff(moment(item.detectionTime)) <
			ZONE_ENTITIES_INACTIVE_THRESHOLD
		);
	});

const mergeDiscoveryUpdate = (currentDiscoveries, newDiscoveries) =>
	unionBy(newDiscoveries, currentDiscoveries, 'clientReference');

// ------------------------------------
// Action Creators
// ------------------------------------
export const listSites = createWsAction({
	type: SITES_LIST_FETCH,
	messageType: 'core.site.list-sites',
	selectorKey: 'sitesList',
	fields: ['clientId'],
});

export const listAllSites = createWsAction({
	type: SITES_LIST_FETCH,
	messageType: 'core.site.list-all-sites',
	selectorKey: 'allSitesList',
	fields: ['clientId'],
});

export const getSite = createWsAction({
	type: SITE_GET,
	messageType: 'core.site.get-site',
	selectorKey: 'siteDetails',
	fields: ['clientId','targetClientId', 'siteId']
})

export const updateSite = createWsAction({
	type: SITE_UPDATE,
	messageType: 'core.site.edit-site',
	selectorKey: 'siteDetails',
	fields: ['clientId','targetClientId', 'siteId', 'changes']
})

export const createSite = createWsAction({
	type: SITE_CREATE,
	messageType: 'core.site.create-site',
	selectorKey: 'siteDetail',
	fields: ['clientId','siteId','description','address','timeZone','liveVisitGracePeriod'],
});

export const listZones = createWsAction({
	type: ZONE_LIST_FETCH,
	messageType: 'core.site.list-zones',
	selectorKey: 'zonesInfo',
	fields: ['clientId', 'siteId'],
	payloadOnSuccess: payload => ({
		...payload,
		data: referenceMap(payload.data, 'zoneId'),
	}),
});

export const listCheckinCheckoutZones = createWsAction({
	type: CHECKIN_CHECKOUT_ZONE_LIST_FETCH,
	messageType: 'core.site.list-checkin-checkout-zones',
	selectorKey: 'zonesCheckinCheckoutInfo',
	fields: ['clientId']
});

export const listAllZones = createWsAction({
	type: ZONES_LIST_FETCH,
	messageType: 'core.site.list-all-zones',
	selectorKey: 'allZonesList',
	fields: ['clientId'],
});

export const getZone = createWsAction({
	type: ZONE_GET,
	messageType: 'core.site.get-zone',
	selectorKey: 'zoneDetails',
	fields: ['clientId','targetClientId', 'siteId', 'zoneId']
})

export const updateZone = createWsAction({
	type: ZONE_UPDATE,
	messageType: 'core.site.edit-zone',
	selectorKey: 'siteDetails',
	fields: ['clientId','targetClientId', 'siteId', 'zoneId', 'changes']
})

export const createZone = createWsAction({
	type: ZONE_CREATE,
	messageType: 'core.site.create-zone',
	selectorKey: 'zoneDetail',
	fields: ['clientId','siteId','zoneId','name','description'],
});

export const listAllReaders = createWsAction({
	type: READERS_LIST_FETCH,
	messageType: 'core.site.list-all-readers',
	selectorKey: 'allReadersList',
	fields: ['clientId'],
});

export const createReader = createWsAction({
	type: READER_CREATE,
	messageType: 'core.site.create-reader',
	selectorKey: 'readerDetail',
	fields: ['clientId','siteId','readerSn','agentId','endpoint'],
});

export const getReader = createWsAction({
	type: READER_GET,
	messageType: 'core.site.get-reader',
	selectorKey: 'readerDetails',
	fields: ['clientId','targetClientId', 'siteId', 'readerSn']
})

export const updateReader = createWsAction({
	type: READER_UPDATE,
	messageType: 'core.site.edit-reader',
	selectorKey: 'readerDetails',
	fields: ['clientId','targetClientId', 'siteId', 'readerSn', 'changes']
})

export const listAllAntennas = createWsAction({
	type: ANTENNAS_LIST_FETCH,
	messageType: 'core.site.list-all-antennas',
	selectorKey: 'allAntennasList',
	fields: ['clientId'],
});

export const createAntenna = createWsAction({
	type: ANTENNA_CREATE,
	messageType: 'core.site.create-antenna',
	selectorKey: 'antennaDetail',
	fields: ['clientId','readerSn','portNumber','siteId','zoneId', 'active'],
});

export const accumulateZonesBySite = createWsAction({
	type: ZONE_LIST_ACCUMULATE_BY_SITE,
	messageType: 'core.site.list-zones',
	selectorKey: 'accumulatedZonesBySite',
	subKey: 'siteId',
	fields: ['clientId', 'siteId'],
});

export const listenToSubscriptions = createWsAction({
	type: ZONE_ENTITIES_SUBSCRIBE,
	messageType: 'core.site.subscribe-detections',
	selectorKey: 'liveDiscovery',
	fields: ['clientId', 'siteId', 'includeLastKnown'],
	defaultParams: {
		includeLastKnown: true,
	},
	subscribe: true,
	payloadOnSuccess: (payload, getState) => ({
		...payload,
		data: mergeDiscoveryUpdate(
			liveDiscoverySelector(getState()),
			payload.data,
		),
	}),
});

export const listenToThermalSubscriptions = createWsAction({
	type: THERMAL_ENTITIES_SUBSCRIBE,
	messageType: 'thermal.site.subscribe-thermal-events',
	selectorKey: 'liveThermalDiscovery',
	fields: ['clientId', 'siteId', 'includeLastKnown'],
	defaultParams: {
		includeLastKnown: true,
	},
	subscribe: true,
	payloadOnSuccess: (payload, getState) => ({
		...payload,
		data: mergeDiscoveryUpdate(
			liveThermalDiscoverySelector(getState()),
			payload.data,
		),
	}),
});

export const listenToSecuritySubscriptions = createWsAction({
	type: THERMAL_ENTITIES_SUBSCRIBE,
	messageType: 'thermal.site.subscribe-security-messages',
	selectorKey: 'securityMessaging',
	fields: ['clientId', 'siteId', 'includeLastKnown'],
	defaultParams: {
		includeLastKnown: true,
	},
	subscribe: true,
	payloadOnSuccess: (payload, getState) => {
		const data = {
			...payload,
			data: unionBy(
				payload.data,
				liveSecurityMessagingSelector(getState()),
				'createdAt',
			).sort((a, b) =>
				moment(a.createdAt).isAfter(moment(b.createdAt)) ? a : b,
			),
		};
		// keep only 100 messages when new messages are arriving;
		return data.length > 100 ? data.slice(0, 100) : data;
	},
});

export const listenToFaceSubscriptions = createWsAction({
	type: FACE_ENTITIES_SUBSCRIBE,
	messageType: 'thermal.face.subscribe-face-events',
	selectorKey: 'liveFaceDiscovery',
	fields: ['clientId', 'siteId', 'includeLastKnown'],
	defaultParams: {
		includeLastKnown: true,
	},
	subscribe: true,
	payloadOnSuccess: (payload, getState) => ({
		...payload,
		data: mergeDiscoveryUpdate(
			liveFaceDiscoverySelector(getState()),
			payload.data,
		),
	}),
});

export const listenToWifiSubscriptions = createWsAction({
	type: WIFI_ENTITIES_SUBSCRIBE,
	messageType: 'core.site.subscribe-wifi-detections',
	selectorKey: 'liveWifiDiscovery',
	fields: ['clientId', 'siteId', 'includeLastKnown'],
	defaultParams: {
		includeLastKnown: true,
	},
	subscribe: true,
	payloadOnSuccess: (payload, getState) => ({
		...payload,
		data: mergeDiscoveryUpdate(
			liveWifiDiscoverySelector(getState()),
			payload.data,
		),
	}),
});

export const clearInactiveEntities = () => (dispatch, getState) =>
	dispatch({
		type: API_SUBSCRIBE_UPDATE,
		payload: {
			selectorKey: 'liveDiscovery',
			data: filterOutInactiveEntities(liveDiscoverySelector(getState())),
		},
	});



// WEBPACK FOOTER //
// ./src/redux/modules/model/site.js